import { useCallback, useMemo, useState } from "react";

import type { LineGraphProps } from "@similarweb/pro-logic-components";
import { colorsSets } from "@similarweb/styles";

import { useDomainColorMap } from "../useDomainColorMap";

import { FormattedLegendItem } from "./FormattedLegendItem";
import type { DataItem, MetaData } from "./types";
import { computeNextMetaData } from "./utils/computeNextMetaData";
import { initMetaData } from "./utils/initMetaData";

const othersColor = colorsSets.c.others;

export type UseControlledGraphDataParams = {
    data: Omit<DataItem, "color" | "visible" | "customLegendComponent">[];
};

export const useControlledGraphData = ({ data }: UseControlledGraphDataParams) => {
    const domainColorMap = useDomainColorMap();

    const [metaData, setMetaData] = useState<MetaData>(() =>
        initMetaData({ data, availableColors: colorsSets.c.toArray(), othersColor }),
    );

    const legendToggleHandlerCallback = useCallback<Required<LineGraphProps<DataItem>>["legendToggleHandlerCallback"]>(
        (filteredData) => {
            const newVisibleIds = new Set<string>((filteredData ?? []).map((item: DataItem) => item.id));
            setMetaData((current) => computeNextMetaData({ current, newVisibleIds, othersColor }));
        },
        [],
    );

    const colorsMap = domainColorMap ?? metaData.colorsMap;

    const controlledData: DataItem[] = useMemo(
        () =>
            data.map((item) => ({
                ...item,
                visible: metaData.visibleIds.has(item.id),
                color: colorsMap[item.id] ?? othersColor,
                customLegendComponent: FormattedLegendItem,
            })),
        [data, colorsMap, metaData.visibleIds],
    );

    return {
        controlledData,
        legendToggleHandlerCallback,
    };
};
